import { fg } from '@atlaskit/platform-feature-flags';
import type { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';
import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import { confluencePagesQuery } from '@atlassian/search-client/content';
import {
	type SearchEntitiesProductARI,
	searchEntitiesQuery,
} from '@atlassian/search-client/entities';
import {
	confluenceDcContentFilterOptionsQuery,
	confluenceDcPagesQuery,
	documentARIEntitiesQuery,
} from '@atlassian/search-client/search-page';
import { cleanExcerpt } from '@atlassian/search-common/search-result-utils';

import { DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI } from '../../../../../common/constants/default-cloud-graph-document-entity-ati';
import { type CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type {
	SelectFilterMonad,
	SelectFilterState,
} from '../../../../../common/constants/filters/select-filter/types';
import { NounSubtypeKey } from '../../../../../common/constants/filters/universal-type';
import { ProductKeys, type ProductKeys1P } from '../../../../../common/constants/product-keys';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { createSingleSelectFilter } from '../../../../filters/single-select-filter';

const getContentId = (ari: string) => {
	const EXTRACT_ID = /ari:.*?:.*?:.*?:.*?\/(.*)?.*/;
	const matches = ari.match(EXTRACT_ID);
	// content id of the ari
	return matches && matches.length > 1 ? matches[1] : '';
};

const searchContent = async (query: string, config: CloudConfig) => {
	const location: SearchEntitiesProductARI = `ari:cloud:confluence::site/${config.cloudId}`;
	const headers = buildAtlAttributionHeaders({
		cloudId: config.cloudId,
		activationId: config.activationId,
		product: config.primaryProduct,
	});

	const product = config.product;
	const selectedSpaceAris = getSelectedSpaceAris(config);
	if (isThirdPartyProductWithContentUnder(product)) {
		const selectedSpaceAri = selectedSpaceAris[0];
		const datasource = config.selectedDatasource ?? config.thirdPartyConfigs?.[product];
		if (!datasource?.integrationAri) {
			return [];
		}
		const spaceFilter = selectedSpaceAri ? { ancestorAris: [selectedSpaceAri] } : {};
		const contributorAccountIds = fg('rovo_fps_scope_3p_typeahead_by_contributor')
			? getSelectedContributorAccountIds(config)
			: [];
		const commonFilters =
			contributorAccountIds.length > 0 ? { contributorsFilter: contributorAccountIds } : undefined;
		const response = await searchEntitiesQuery({
			aggAbsoluteUrl: config.aggAbsoluteUrl,
			variables: {
				query,
				first: 10,
				experience: 'search-page',
				filters: {
					entities: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
					locations: [location],
					...(commonFilters && { commonFilters }),
					thirdPartyFilters: {
						titleMatchOnly: true,
						...spaceFilter,
						thirdPartyProducts: [
							{
								product,
								integrationId: datasource.integrationAri,
								subtypes: getThirdPartyContentUnderSubtypes(product),
							},
						],
					},
				},
			},
			headers,
			includeContainerId: true,
		});

		const results =
			response.data?.search?.results.edges.map((content) => {
				// Access entity.containerId for cross-filter validation (folder belongs to which drive/space)
				const containerId = content.node.entity?.containerId;
				const option = {
					trackingKey: content.node.id,
					value: content.node.id,
					label: content.node.title,
					queryParamValue: content.node.id,
					product: config.product,
					...(containerId && {
						metadata: { containerARI: containerId },
					}),
				};
				return option;
			}) || [];
		return results;
	}

	let contentPagesResponse;
	if (product === ProductKeys.ConfluenceDC) {
		contentPagesResponse = await confluenceDcContentFilterOptionsQuery({
			variables: {
				query,
				cloudIdARI: location,
				limit: 10,
				entities: ['ati:cloud:graph:document'],
				product,
			},
			aggAbsoluteUrl: config.aggAbsoluteUrl,
			headers,
		});

		return (
			contentPagesResponse.data?.search?.search?.edges.map((content) => ({
				trackingKey: content.node.id,
				value: content.node.id,
				label: cleanExcerpt(content.node.title),
				queryParamValue: content.node.id,
				product: config.product,
			})) || []
		);
	}

	contentPagesResponse = await searchEntitiesQuery({
		aggAbsoluteUrl: config.aggAbsoluteUrl,
		variables: {
			query,
			first: 10,
			filters: {
				entities: ['ati:cloud:confluence:page'],
				locations: [location],
			},
		},
		headers,
	});

	const allContentPages =
		contentPagesResponse.data?.search?.results.edges.map((content) => ({
			title: content.node.title,
			id: content.node.id,
		})) || [];

	const contentOptions = allContentPages.map((content) => {
		const contentId = getContentId(content.id);
		return {
			trackingKey: contentId,
			value: contentId,
			label: content.title,
			queryParamValue: contentId,
			product: config.product,
		};
	});

	return contentOptions;
};

const productKeys1P: ProductKeys1P[] = [ProductKeys.Confluence];

const getProducts3PFromConfig = (): ProductKeys3P[] => getProducts3PFromConfigForFilter('content');
const getFilterProducts = (): ProductKeys[] => [...productKeys1P, ...getProducts3PFromConfig()];
const THIRD_PARTY_CONTENT_UNDER_SUBTYPES: Partial<Record<ProductKeys3P, NounSubtypeKey[]>> = {
	[ProductKeys.GoogleDrive]: [NounSubtypeKey.Folder],
	[ProductKeys.Sharepoint]: [NounSubtypeKey.Drive, NounSubtypeKey.Folder],
};
const isThirdPartyProductWithContentUnder = (
	product?: string,
): product is keyof typeof THIRD_PARTY_CONTENT_UNDER_SUBTYPES =>
	!!product &&
	product in THIRD_PARTY_CONTENT_UNDER_SUBTYPES &&
	getProducts3PFromConfig().includes(product as ProductKeys3P);
const getThirdPartyContentUnderSubtypes = (
	product: keyof typeof THIRD_PARTY_CONTENT_UNDER_SUBTYPES,
) => THIRD_PARTY_CONTENT_UNDER_SUBTYPES[product] ?? [];
const getSelectedSpaceAris = (config: CloudConfig): string[] =>
	Array.isArray(config.initialSpaceARIs) ? config.initialSpaceARIs.filter(Boolean) : [];
const getSelectedContributorAccountIds = (config: CloudConfig): string[] =>
	Array.isArray(config.contributorAccountIds) ? config.contributorAccountIds.filter(Boolean) : [];
const withCurrentProducts = (
	state: SelectFilterState<'content'>,
): SelectFilterState<'content'> => ({
	...state,
	products: getFilterProducts(),
});
const withCurrentProductsFilter = (
	filter: SelectFilterMonad<'content'>,
): SelectFilterMonad<'content'> => ({
	...filter,
	getState: () => withCurrentProducts(filter.getState()),
	queryParams: {
		...filter.queryParams,
		set: async (queryParams, onStateChange) =>
			withCurrentProductsFilter(
				await filter.queryParams.set(
					queryParams,
					onStateChange &&
						((state) => onStateChange(withCurrentProducts(state as SelectFilterState<'content'>))),
				),
			),
	},
	loadInitialOptions: async (onStateChange) =>
		withCurrentProductsFilter(
			await filter.loadInitialOptions(
				onStateChange && ((state) => onStateChange(withCurrentProducts(state))),
			),
		),
	setOption: (option, customValue) =>
		withCurrentProductsFilter(filter.setOption(option, customValue)),
	clearOptions: () => withCurrentProductsFilter(filter.clearOptions()),
	setLookupInput: async (query, onStateChange) =>
		withCurrentProductsFilter(
			await filter.setLookupInput(
				query,
				onStateChange && ((state) => onStateChange(withCurrentProducts(state))),
			),
		),
	clear: () => withCurrentProductsFilter(filter.clear()),
});

const baseFilter = createSingleSelectFilter({
	id: 'content',
	products: getFilterProducts(),
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'ancestor',
		fetcher: async (queryParams, config) => {
			const product = config.product;
			const location: SearchEntitiesProductARI = `ari:cloud:confluence::site/${config.cloudId}`;
			const headers = buildAtlAttributionHeaders({
				cloudId: config.cloudId,
				activationId: config.activationId,
				product: config.primaryProduct,
			});

			if (isThirdPartyProductWithContentUnder(product)) {
				const contentResponse = await documentARIEntitiesQuery({
					variables: { ids: queryParams },
					aggAbsoluteUrl: config.aggAbsoluteUrl,
					headers,
				});

				return (
					contentResponse.data?.external_entities?.document?.map((content) => ({
						trackingKey: content.id,
						value: content.id,
						label: content.displayName,
						queryParamValue: content.id,
						product: config.product,
						...(content.container?.id && {
							metadata: { containerARI: content.container.id },
						}),
					})) || []
				);
			}

			if (product === ProductKeys.ConfluenceDC) {
				const contentPagesResponse = await confluenceDcPagesQuery({
					variables: {
						query: '',
						cloudIdARI: location,
						limit: 10,
						entities: ['ati:cloud:graph:document'],
						product,
						ancestorIds: queryParams,
					},
					aggAbsoluteUrl: config.aggAbsoluteUrl,
					headers,
				});

				return (
					contentPagesResponse.data?.external_entities?.document?.map((content) => ({
						trackingKey: content.id,
						value: content.id,
						label: content.displayName,
						queryParamValue: content.id,
						product: config.product,
					})) || []
				);
			}

			const initialContentARIs = queryParams.map(
				(contentId) => `ari:cloud:confluence:${config.cloudId}:page/${contentId}`,
			);

			const initialContentResponse = await confluencePagesQuery(initialContentARIs, headers);

			const allContentPages =
				initialContentResponse.data?.confluence.pages.map((page) => ({
					title: page.title,
					id: page.id,
				})) || [];

			const contentOptions = allContentPages.map((content) => {
				const contentId = getContentId(content.id);
				return {
					trackingKey: contentId,
					value: contentId,
					label: content.title,
					queryParamValue: contentId,
					product: config.product,
				};
			});
			return contentOptions;
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return searchContent('', config);
		},
	},
	lookup: {
		fetcher: async (query, config) => {
			return searchContent(query, config);
		},
	},
});

export const filter: Readonly<{
	id: 'content';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'content'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (state: unknown, config: CloudConfig) => SelectFilterMonad<'content'>;
	getInitialState: (config: CloudConfig) => SelectFilterMonad<'content'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = {
	...baseFilter,
	queryParams: {
		...baseFilter.queryParams,
		getState: (queryParams, cloudId) =>
			withCurrentProducts(baseFilter.queryParams.getState(queryParams, cloudId)),
	},
	get products() {
		return getFilterProducts();
	},
	withState: (state, config) =>
		withCurrentProductsFilter(
			baseFilter.withState(
				{
					...(state as SelectFilterState<'content'>),
					products: getFilterProducts(),
				},
				config,
			),
		),
	getInitialState: (config) => withCurrentProductsFilter(baseFilter.getInitialState(config)),
};
